import { Flex } from '@assets/component/UI/Flex/Flex';
import { LogoBird } from '@assets/component/Header/Logo';
import { getFirebase } from '@assets/firebase/FirebaseAdapater';
import {
    StagingMetadata,
    StagingTurn,
    useStore,
    useTasksStore
} from '@assets/model/Store';
import { onValue, ref } from 'firebase/database';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import * as style from './ChatMessageStagingProposal.module.less';

interface ChatMessageStagingProposalProps {
    taskId: string;
    candidate: string;
    metadata: StagingMetadata | null;
}

// Comparator prompt emits "## Change Log ... ## Revised Artifact ..." — split
// on whichever marker variant the LLM produced so we can render the bulleted
// summary above the diff and keep the full revised content below.
function splitCandidate(candidate: string): {
    changeLog: string;
    artifact: string;
} {
    // Accept the "Revised Artifact" header in any of these shapes:
    //   ## Revised Artifact
    //   ## Section 2 — Revised Artifact
    //   **Section 2 — Revised Artifact:**
    //   # Revised Artifact
    const markers = [
        /^#{1,2}\s*(?:Section\s*2\s*[—-]\s*)?Revised\s+Artifact/im,
        /\*\*Section\s*2\s*[—-]\s*Revised\s+Artifact:?\*\*/i
    ];
    let splitIdx = -1;
    let markerMatch: RegExpExecArray | null = null;
    for (const m of markers) {
        const match = m.exec(candidate);
        if (match) {
            splitIdx = match.index;
            markerMatch = match;
            break;
        }
    }
    if (splitIdx === -1) {
        return { changeLog: '', artifact: candidate };
    }
    const before = candidate.slice(0, splitIdx);
    // Skip the marker line itself in the artifact preview
    const afterStart = splitIdx + (markerMatch ? markerMatch[0].length : 0);
    const after = candidate.slice(afterStart).replace(/^\s+/, '');
    const changeLog = before
        .replace(
            /^##?\s*(Section\s*1\s*[—-]\s*)?Change\s+Log:?\s*/im,
            ''
        )
        .replace(
            /^\*\*Section\s*1\s*[—-]\s*Change\s+Log:?\*\*\s*/i,
            ''
        )
        .trim();
    return { changeLog, artifact: after };
}

type DiffLine = { text: string; kind: 'same' | 'added' | 'removed' };

// Line-level diff using Longest Common Subsequence; unified output (one column,
// +/- prefixes) keeps the bubble narrow enough for chat layout.
function lineDiff(current: string, candidate: string): DiffLine[] {
    const a = (current || '').split('\n');
    const b = (candidate || '').split('\n');
    const n = a.length;
    const m = b.length;
    const dp: number[][] = Array.from({ length: n + 1 }, () =>
        new Array(m + 1).fill(0)
    );
    for (let i = n - 1; i >= 0; i--) {
        for (let j = m - 1; j >= 0; j--) {
            if (a[i] === b[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
            else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
        }
    }
    const out: DiffLine[] = [];
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
        if (a[i] === b[j]) {
            out.push({ text: a[i], kind: 'same' });
            i++;
            j++;
        } else if (dp[i + 1][j] >= dp[i][j + 1]) {
            out.push({ text: a[i], kind: 'removed' });
            i++;
        } else {
            out.push({ text: b[j], kind: 'added' });
            j++;
        }
    }
    while (i < n) out.push({ text: a[i++], kind: 'removed' });
    while (j < m) out.push({ text: b[j++], kind: 'added' });
    return out;
}

// Compact a full line-diff into hunks: each block of changed lines plus N
// lines of unchanged context before/after. Long unchanged stretches between
// hunks are replaced with a single "gap" sentinel that renders as "…" in the
// UI. This keeps the diff honest (no edits omitted) while cutting the noise
// of identical sections — the same idea as `git diff -U2`.
type DiffEntry =
    | { kind: 'same' | 'added' | 'removed'; text: string }
    | { kind: 'gap'; skipped: number };
function compactDiff(lines: DiffLine[], context: number = 2): DiffEntry[] {
    if (lines.length === 0) return [];
    // Mark which lines must be kept: changed lines, plus `context` lines on
    // each side.
    const keep = new Array(lines.length).fill(false);
    for (let i = 0; i < lines.length; i++) {
        if (lines[i].kind !== 'same') {
            const lo = Math.max(0, i - context);
            const hi = Math.min(lines.length - 1, i + context);
            for (let j = lo; j <= hi; j++) keep[j] = true;
        }
    }
    const out: DiffEntry[] = [];
    let i = 0;
    while (i < lines.length) {
        if (keep[i]) {
            out.push({ kind: lines[i].kind, text: lines[i].text });
            i++;
        } else {
            let j = i;
            while (j < lines.length && !keep[j]) j++;
            const skipped = j - i;
            // Only emit a gap marker if we're between hunks — leading and
            // trailing dropped runs at the very start/end of the document are
            // omitted silently (nothing changed there, no need to flag).
            if (out.length > 0 && j < lines.length) {
                out.push({ kind: 'gap', skipped });
            }
            i = j;
        }
    }
    return out;
}

function reasonLabel(r: string): string {
    if (r === 'new_files_in_chat') return 'New file added in this module';
    if (r === 'chat_refine') return 'Refined via chat';
    return r || 'Updated';
}

function formatRelativeTime(ts: number): string {
    if (!ts) return '';
    const diff = Date.now() - ts;
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    const d = Math.floor(hr / 24);
    return `${d} day${d === 1 ? '' : 's'} ago`;
}

export const ChatMessageStagingProposal = observer(
    (p: ChatMessageStagingProposalProps) => {
        const { taskId, candidate, metadata } = p;
        const store = useStore();
        const taskStore = useTasksStore();
        const taskDef = taskStore.get_task_definition(taskId);
        const reportId = store.selectedReportId;

        const [currentArtifact, setCurrentArtifact] = useState<string>('');
        const [diffExpanded, setDiffExpanded] = useState<boolean>(false);
        const [discussOpen, setDiscussOpen] = useState<boolean>(false);
        const [discussInput, setDiscussInput] = useState<string>('');
        // Optimistic pending turn: shown immediately when the user clicks Send
        // so the bubble doesn't appear frozen while the LLM runs. Cleared as
        // soon as the new metadata.turns array arrives from Firebase.
        const [pendingUserMessage, setPendingUserMessage] = useState<
            string | null
        >(null);

        // Persisted turns from metadata. Comparator writes the first one,
        // chat_refine appends. Falls back to the latest-only changeLog when
        // an older staging entry doesn't have a turns array yet.
        const persistedTurns: StagingTurn[] = metadata?.turns ?? [];

        // Clear the optimistic pending entry as soon as Firebase confirms a
        // new turn was persisted (length grew or the most recent entry has
        // the userMessage we just sent). At that point we also clear the
        // textarea so the user can type the next refinement.
        useEffect(() => {
            if (!pendingUserMessage) return;
            const matchesLatest =
                persistedTurns.length > 0 &&
                persistedTurns[persistedTurns.length - 1].userMessage ===
                    pendingUserMessage;
            if (matchesLatest) {
                setPendingUserMessage(null);
                setDiscussInput('');
            }
        }, [persistedTurns.length, pendingUserMessage]);

        useEffect(() => {
            if (!taskDef) return;
            const path = `/flowtasks/vars/${reportId}/${taskDef.save_to}`;
            const fb = getFirebase();
            const r = ref(fb.db, path);
            const unsub = onValue(r, snapshot => {
                const val = snapshot.val();
                setCurrentArtifact(typeof val === 'string' ? val : '');
            });
            return () => unsub();
        }, [reportId, taskDef?.save_to]);

        if (!taskDef) return null;

        const { changeLog, artifact } = splitCandidate(candidate);
        const diff = lineDiff(currentArtifact, artifact);
        // Block-level change counter (canonical vs current candidate). One
        // "block" = a contiguous run of added/removed lines bounded by same
        // lines on both sides. A removed line followed immediately by an
        // added line (typical rewrite of a paragraph) counts as one block,
        // not two. Bullets the LLM reshuffled inside a single section also
        // collapse into one block because they share contiguity.
        let changedCount = 0;
        let inBlock = false;
        for (const l of diff) {
            if (l.kind === 'same') {
                inBlock = false;
            } else if (!inBlock) {
                inBlock = true;
                changedCount += 1;
            }
        }
        // Compact the diff into hunks (changed lines + 2 lines of context)
        // so the expanded view shows just the deltas instead of the whole
        // document. Unchanged stretches between hunks collapse to "…".
        const compactedDiff = compactDiff(diff, 2);

        // The LLM emits inline citations as [[[TITLE: ...|||SOURCE: ...|||NAME:
        // <file>|||PAGES: ...|||URL: ...]]] and prefixes each citation cluster
        // with "*** SOURCES USED:". Rendering raw burns space and is unreadable;
        // collapse each citation into a compact "[file]" chip whose hover (title
        // attribute) reveals the full TITLE/SOURCE/PAGES/URL. Drop the
        // "*** SOURCES USED:" label entirely — the chips already convey what
        // they are.
        // Renumber top-level bullets in the Change Log so each change reads as
        // "1.", "2.", "3." instead of identical "-" bullets. The team finds the
        // numbered form easier to reference in conversation ("the change #2 we
        // disagreed on"). Only the first level is renumbered — nested bullets
        // (indented with two or more spaces) stay as "-" so the visual
        // hierarchy survives.
        const numberTopLevelBullets = (text: string): string => {
            const lines = text.split('\n');
            let counter = 0;
            return lines
                .map(line => {
                    const bulletMatch = /^(\s*)([-*])\s+(.*)$/.exec(line);
                    if (bulletMatch) {
                        const indent = bulletMatch[1];
                        // Top-level "-" or "*" becomes a numbered item.
                        if (indent.length === 0) {
                            counter += 1;
                            return `${counter}. ${bulletMatch[3]}`;
                        }
                        // Nested bullets ("  - Why: ...") need at least 4
                        // spaces of indent so marked treats them as a
                        // sub-list of the numbered parent. Pad short indents
                        // up to 4; preserve deeper indents as-is.
                        if (indent.length < 4) {
                            return `    ${bulletMatch[2]} ${bulletMatch[3]}`;
                        }
                        return line;
                    }
                    // Indented continuation lines of a numbered item (e.g. a
                    // wrapped sentence) need the same 4-space indent so they
                    // stay glued to their parent in the rendered list.
                    const continuation = /^(\s+)(\S.*)$/.exec(line);
                    if (continuation && counter > 0) {
                        const indent = continuation[1];
                        if (indent.length < 4) {
                            return `    ${continuation[2]}`;
                        }
                    }
                    return line;
                })
                .join('\n');
        };
        const convertMarkdown = (md: string): string => {
            const compact = numberTopLevelBullets(md ?? '')
                .replace(/\*\*\*\s*SOURCES?\s+USED\s*:?\s*/gi, '')
                .replace(/\[\[\[([\s\S]*?)\]\]\]/g, (_match, body: string) => {
                    const field = (label: string): string => {
                        const re = new RegExp(
                            `${label}\\s*:\\s*([^|]*?)(?=\\s*\\|\\|\\||$)`,
                            'i'
                        );
                        const m = re.exec(body);
                        return m ? m[1].trim() : '';
                    };
                    const name = field('NAME');
                    const title = field('TITLE');
                    const source = field('SOURCE');
                    const pages = field('PAGES');
                    const url = field('URL');
                    const tooltipParts = [
                        title && `Title: ${title}`,
                        source && `Source: ${source}`,
                        pages && `Pages: ${pages}`,
                        url && `URL: ${url}`
                    ].filter(Boolean);
                    const tooltip = tooltipParts
                        .join('\n')
                        .replace(/"/g, '&quot;');
                    const label = name || title || 'source';
                    return `<a class="${style.citationChip}" title="${tooltip}">${label}</a>`;
                });
            const html = marked.parse(compact, { async: false });
            return DOMPurify.sanitize(html as string, {
                ADD_ATTR: ['title']
            });
        };

        const onAccept = async () => {
            const ok = window.confirm(
                'Apply these changes to the Flightline output? The previous version will be overwritten.'
            );
            if (!ok) return;
            // Build a confirmation echo for the chat thread so accepting the
            // proposal leaves a visible trace instead of silently removing the
            // bubble. Include the accepted change log (same parse the bubble
            // uses) so the user sees exactly what landed in the canonical.
            const summary = changeLog.trim();
            const confirmation = summary
                ? `Changes applied to the Flightline output.\n\n**Section 1 — Change Log:**\n\n${summary}`
                : 'Changes applied to the Flightline output.';
            const fb = getFirebase();
            await fb.vars_staging_accept(
                reportId,
                taskId,
                taskDef.save_to,
                confirmation
            );
        };

        const onReject = async () => {
            const ok = window.confirm(
                'Discard the proposed changes? The Flightline output will be unchanged.'
            );
            if (!ok) return;
            const fb = getFirebase();
            await fb.vars_staging_reject(reportId, taskId, taskDef.save_to);
        };

        const onDiscussSend = async () => {
            const text = discussInput.trim();
            if (!text) return;
            // Optimistic: render the user's instruction immediately as a
            // pending entry. Keep the typed text in the textarea (disabled)
            // so the user keeps visual confirmation of what was sent until
            // the persisted turn arrives — then the useEffect above clears
            // both the pending marker and the input.
            setPendingUserMessage(text);
            const fb = getFirebase();
            fb.flowtask_invoke_chat_refine(reportId, taskId, text);
        };

        return (
            <Flex
                className={`${style.chatMessage} fade-in`}
                direction="row"
                wrap="wrap"
            >
                <div className={style.logoContainer}>
                    <LogoBird />
                </div>
                <div className={style.bubble}>
                    <div className={style.title}>
                        Proposed update — {taskDef.name}
                    </div>

                    {(persistedTurns.length > 0 ||
                        pendingUserMessage) && (
                        <div className={style.timelineSection}>
                            {persistedTurns.map((turn, idx) => {
                                const isInitial =
                                    turn.source === 'comparator' ||
                                    !turn.userMessage;
                                const sources =
                                    turn.sources
                                        ?.map(f => f.split('/').pop() || f)
                                        .filter(Boolean) ?? [];
                                return (
                                    <div
                                        key={`p${idx}`}
                                        className={style.timelineTurn}
                                    >
                                        <div
                                            className={
                                                style.timelineTurnHeader
                                            }
                                        >
                                            <span className={style.turnIndex}>
                                                {idx + 1}
                                            </span>
                                            <div
                                                className={style.turnHeaderText}
                                            >
                                                {isInitial ? (
                                                    <>
                                                        <div
                                                            className={
                                                                style.turnTitle
                                                            }
                                                        >
                                                            Initial proposal
                                                            from new evidence
                                                        </div>
                                                        <div
                                                            className={
                                                                style.turnSubtitle
                                                            }
                                                        >
                                                            {reasonLabel(
                                                                turn.reason ||
                                                                    ''
                                                            )}
                                                            {sources.length >
                                                                0 && (
                                                                <>
                                                                    {' · '}
                                                                    {sources.join(
                                                                        ', '
                                                                    )}
                                                                </>
                                                            )}
                                                            {' · '}
                                                            {formatRelativeTime(
                                                                turn.timestamp
                                                            )}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div
                                                            className={
                                                                style.turnTitle
                                                            }
                                                        >
                                                            <strong>
                                                                You:
                                                            </strong>{' '}
                                                            {turn.userMessage}
                                                        </div>
                                                        <div
                                                            className={
                                                                style.turnSubtitle
                                                            }
                                                        >
                                                            Refined via chat
                                                            {' · '}
                                                            {formatRelativeTime(
                                                                turn.timestamp
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div className={style.timelineTurnBody}>
                                            {turn.changeLog ? (
                                                <div
                                                    className={
                                                        style.turnChangeLogContent
                                                    }
                                                    dangerouslySetInnerHTML={{
                                                        __html: convertMarkdown(
                                                            turn.changeLog
                                                        )
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    className={style.turnEmpty}
                                                >
                                                    No material changes
                                                    captured.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {pendingUserMessage && (
                                <div
                                    key="pending"
                                    className={style.timelineTurn}
                                >
                                    <div className={style.timelineTurnHeader}>
                                        <span className={style.turnIndex}>
                                            {persistedTurns.length + 1}
                                        </span>
                                        <div className={style.turnHeaderText}>
                                            <div className={style.turnTitle}>
                                                <strong>You:</strong>{' '}
                                                {pendingUserMessage}
                                            </div>
                                            <div
                                                className={style.turnSubtitle}
                                            >
                                                Refining via chat · just now
                                            </div>
                                        </div>
                                    </div>
                                    <div className={style.timelineTurnBody}>
                                        <div className={style.turnPending}>
                                            <i className="bi bi-arrow-clockwise"></i>{' '}
                                            Agent is refining…
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div
                        className={style.diffToggle}
                        onClick={() => setDiffExpanded(!diffExpanded)}
                    >
                        <i
                            className={`bi ${diffExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`}
                        ></i>
                        <span>
                            {diffExpanded ? 'Hide' : 'Show'} full diff (
                            {changedCount}{' '}
                            {changedCount === 1 ? 'change' : 'changes'})
                        </span>
                    </div>

                    {diffExpanded && (
                        <div className={style.diffContainer}>
                            {compactedDiff.map((entry, idx) => {
                                if (entry.kind === 'gap') {
                                    return (
                                        <div
                                            key={idx}
                                            className={style.lineGap}
                                            title={`${entry.skipped} unchanged ${entry.skipped === 1 ? 'line' : 'lines'} hidden`}
                                        >
                                            <span className={style.gutter}>
                                                …
                                            </span>
                                            <span className={style.lineText}>
                                                {entry.skipped} unchanged{' '}
                                                {entry.skipped === 1
                                                    ? 'line'
                                                    : 'lines'}
                                            </span>
                                        </div>
                                    );
                                }
                                return (
                                    <div
                                        key={idx}
                                        className={
                                            entry.kind === 'added'
                                                ? style.lineAdded
                                                : entry.kind === 'removed'
                                                  ? style.lineRemoved
                                                  : style.lineSame
                                        }
                                    >
                                        <span className={style.gutter}>
                                            {entry.kind === 'added'
                                                ? '+'
                                                : entry.kind === 'removed'
                                                  ? '-'
                                                  : ' '}
                                        </span>
                                        <span className={style.lineText}>
                                            {entry.text}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {discussOpen && (
                        <div className={style.discussSection}>
                            <div className={style.discussLabel}>
                                Refine the proposal
                            </div>
                            <textarea
                                className={style.discussInput}
                                value={discussInput}
                                onChange={e =>
                                    setDiscussInput(e.currentTarget.value)
                                }
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        onDiscussSend();
                                    }
                                }}
                                placeholder='e.g. "Move Acme to Tier 1" or "Drop the Trivium row"'
                                disabled={pendingUserMessage !== null}
                                autoFocus
                            />
                            <Flex
                                direction="row"
                                justify="end"
                                style={{ width: '100%', gap: 8, marginTop: 8 }}
                            >
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => {
                                        setDiscussOpen(false);
                                        setDiscussInput('');
                                    }}
                                    disabled={pendingUserMessage !== null}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={onDiscussSend}
                                    disabled={
                                        pendingUserMessage !== null ||
                                        !discussInput.trim()
                                    }
                                >
                                    Send
                                </Button>
                            </Flex>
                        </div>
                    )}

                    <Flex
                        direction="row"
                        justify="end"
                        style={{
                            width: '100%',
                            marginTop: 16,
                            gap: 8
                        }}
                    >
                        <Button variant="outline-danger" onClick={onReject}>
                            Reject
                        </Button>
                        <Button
                            variant="outline-secondary"
                            onClick={() => setDiscussOpen(v => !v)}
                            disabled={pendingUserMessage !== null}
                        >
                            {discussOpen ? 'Hide Discuss' : 'Discuss'}
                        </Button>
                        <Button variant="success" onClick={onAccept}>
                            Accept
                        </Button>
                    </Flex>
                </div>
            </Flex>
        );
    }
);
